import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPinned,
  ArrowRight,
  Settings2,
  Map as MapIcon,
  ShieldCheck,
  Navigation,
  Share2,
  Radio,
  Layers,
  Check,
  Wine,
  Flame,
  Cookie,
  Zap,
} from "lucide-react";
import { useData } from "@/context/DataContext";
import { catColor, CategoryIcon } from "@/lib/categories";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/e2de2b38-bbd9-4485-b70b-c05a8b70bfed/images/44d6648ec77a8c6390c61548eea107c05cc29752c7818eced2dc9dfa5b62bd5d.jpeg";
const OPS_IMG =
  "https://static.prod-images.emergentagent.com/jobs/e2de2b38-bbd9-4485-b70b-c05a8b70bfed/images/c15e78627fae748c7b189cca4835fb56f1b9c1f847358422edbd941ccc2d6741.jpeg";

const NO_CAT = "Ohne Kategorie";

const GROUP_CARDS = [
  { key: "weine", title: "Weine & Sekt", desc: "Alle Weinstände & Sektbars", icon: Wine, color: "#7C1D3F" },
  { key: "herzhaft", title: "Herzhaft & Grill", desc: "Wurst, Grill, Flammkuchen, Fisch & Käse", icon: Flame, color: "#C2410C" },
  { key: "suesses", title: "Süßes & Gebäck", desc: "Crêpes, Süßspeisen, Backwaren", icon: Cookie, color: "#B45309" },
  { key: "infrastruktur", title: "Infrastruktur", desc: "Sperren & Stromverteiler", icon: Zap, color: "#0F172A" },
];

function Feature({ icon: Icon, title, children, testid }) {
  return (
    <div
      className="go-fade-up rounded-3xl border border-slate-200 bg-white p-7 shadow-[0_12px_40px_rgba(15,23,42,0.05)] transition-transform hover:-translate-y-1"
      data-testid={testid}
    >
      <div className="mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-amber-100 text-primary">
        <Icon size={22} strokeWidth={2.2} />
      </div>
      <h3 className="mb-1.5 font-display text-lg font-bold text-slate-900">{title}</h3>
      <p className="text-sm leading-relaxed text-slate-600">{children}</p>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-3 text-sm leading-relaxed text-slate-300">
      <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/20 text-primary">
        <Check size={13} strokeWidth={3} />
      </span>
      {children}
    </li>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { items, categories, rootTitle } = useData();

  const located = useMemo(
    () => (items || []).filter((i) => i.latitude != null && i.longitude != null),
    [items]
  );

  const topCats = useMemo(() => {
    const m = {};
    located.forEach((i) => {
      const c = i.category || NO_CAT;
      m[c] = (m[c] || 0) + 1;
    });
    return Object.entries(m)
      .filter(([name]) => name !== NO_CAT)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, count]) => ({ name, count }));
  }, [located]);

  const title = rootTitle || "Weinwoche Wiesbaden 2026";

  useEffect(() => {
    document.title = `${title} · Smarte Lagekarte`;
  }, [title]);

  return (
    <div
      className="min-h-[100dvh] w-full overflow-x-hidden bg-[#F4F6FB] text-slate-900"
      data-testid="landing-page"
    >
      {/* Top nav */}
      <header className="absolute inset-x-0 top-0 z-30 flex items-center justify-between px-5 py-5 sm:px-8">
        <div className="flex items-center gap-2 text-white">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-white shadow-lg shadow-slate-900/40">
            <MapPinned size={18} />
          </span>
          <span className="font-display text-base font-extrabold tracking-tight drop-shadow">
            {title}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/admin")}
            data-testid="nav-admin-link"
            aria-label="Verwaltung"
            className="grid h-10 w-10 place-items-center rounded-xl bg-white/15 text-white backdrop-blur-md transition-colors hover:bg-white/25"
          >
            <Settings2 size={18} />
          </button>
          <button
            onClick={() => navigate("/map")}
            data-testid="nav-cta-map"
            className="flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-bold text-slate-900 shadow-lg transition-transform hover:scale-[1.03] active:scale-[0.97]"
          >
            <MapIcon size={16} /> Zur Karte
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative isolate flex min-h-[92vh] flex-col justify-end overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Vernetzte Lagekarte einer Outdoor-Veranstaltung bei Nacht"
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#070c18] via-[#070c18]/70 to-[#070c18]/30" />

        <div className="mx-auto w-full max-w-6xl px-5 pb-16 pt-28 sm:px-8 sm:pb-24">
          <div className="go-fade-up max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-white backdrop-blur">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              {title} · Live Lagekarte
            </span>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Kritische Infrastruktur.
              <span className="block text-primary">Smart vernetzt.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-200/90 sm:text-lg">
              Die interaktive Lagekarte für Outdoor-Veranstaltungen: alle
              sicherheitsrelevanten Punkte, Versorgung und Orientierung – digital
              vernetzt, live aktualisiert und auf einen Blick verständlich.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={() => navigate("/map")}
                data-testid="hero-cta-map"
                className="group flex items-center gap-2.5 rounded-2xl bg-primary px-7 py-4 text-base font-bold text-white shadow-xl shadow-black/40 transition-transform hover:scale-[1.03] active:scale-[0.97]"
              >
                <MapIcon size={20} />
                Lagekarte öffnen
                <ArrowRight
                  size={19}
                  className="transition-transform group-hover:translate-x-1"
                />
              </button>
              <button
                onClick={() => navigate("/admin")}
                data-testid="hero-cta-admin"
                className="flex items-center gap-2 rounded-2xl border border-white/30 bg-white/10 px-6 py-4 text-base font-semibold text-white backdrop-blur-md transition-colors hover:bg-white/20"
              >
                <Settings2 size={18} /> Verwaltung
              </button>
            </div>

            {located.length > 0 && (
              <div className="mt-10 flex gap-8" data-testid="hero-stats">
                <div>
                  <p className="font-display text-3xl font-extrabold text-white">
                    {located.length}
                  </p>
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                    vernetzte Punkte
                  </p>
                </div>
                <div>
                  <p className="font-display text-3xl font-extrabold text-white">
                    {categories?.length || topCats.length}
                  </p>
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                    Kategorien
                  </p>
                </div>
                <div>
                  <p className="font-display text-3xl font-extrabold text-white">Live</p>
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                    Echtzeit
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Visitor selection groups */}
      <section
        className="mx-auto w-full max-w-6xl px-5 pt-16 sm:px-8 sm:pt-24"
        data-testid="visitor-groups"
      >
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Für Besucher</p>
        <h2 className="mt-2 max-w-2xl font-display text-2xl font-bold text-slate-900 sm:text-3xl">
          Wonach suchst du?
        </h2>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-600">
          Wähle eine Kategorie – wir zeigen dir direkt die passenden Stände auf der Karte.
        </p>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {GROUP_CARDS.map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.key}
                onClick={() => navigate(`/map?group=${c.key}`)}
                data-testid={`group-${c.key}`}
                className="group flex flex-col items-start rounded-3xl border border-slate-200 bg-white p-6 text-left shadow-[0_12px_40px_rgba(15,23,42,0.05)] transition-transform hover:-translate-y-1"
              >
                <span
                  className="mb-4 grid h-14 w-14 place-items-center rounded-2xl text-white"
                  style={{ background: c.color }}
                >
                  <Icon size={26} strokeWidth={2.1} />
                </span>
                <h3 className="font-display text-lg font-bold text-slate-900">{c.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">{c.desc}</p>
                <span className="mt-4 flex items-center gap-1.5 text-sm font-bold text-primary">
                  Auf der Karte ansehen
                  <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto w-full max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
          Warum diese Karte
        </p>
        <h2 className="mt-2 max-w-2xl font-display text-2xl font-bold text-slate-900 sm:text-3xl">
          Kritische Infrastruktur intelligent sichtbar gemacht
        </h2>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <Feature icon={Share2} title="Alles vernetzt" testid="feature-connected">
            Jeder Standort – von Stromverteilung bis Rettungspunkt – wird digital
            verknüpft und zentral auf einer Karte zusammengeführt.
          </Feature>
          <Feature icon={ShieldCheck} title="Sicherheit im Blick" testid="feature-safety">
            Feuerwehr, Sammelplätze, Notausgänge und Sperren sind farbcodiert und in
            Sekunden auffindbar – für schnelle Entscheidungen.
          </Feature>
          <Feature icon={Navigation} title="Punktgenaue Infos" testid="feature-nav">
            Zu jedem Punkt gibt es exakte Koordinaten, eine what3words-Adresse und
            wissensreiche Details – kein Suchen, kein Raten.
          </Feature>
          <Feature icon={Radio} title="Live & aktuell" testid="feature-live">
            Änderungen am Gelände erscheinen sofort. Alle Beteiligten sehen jederzeit
            denselben, aktuellen Stand.
          </Feature>
        </div>
      </section>

      {/* Showcase */}
      <section className="bg-[#070c18] py-16 sm:py-24" data-testid="showcase">
        <div className="mx-auto grid w-full max-w-6xl items-center gap-10 px-5 sm:px-8 lg:grid-cols-2">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
              Gemacht für Outdoor-Veranstaltungen
            </p>
            <h2 className="mt-3 font-display text-2xl font-bold text-white sm:text-3xl">
              Ein gemeinsames Lagebild für das ganze Team
            </h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-slate-300 sm:text-base">
              Ob Sicherheitsplanung, Betriebsleitung oder Aufbau-Team – alle greifen
              auf dieselbe smarte Karte zu. Kritische Infrastruktur wird nicht in
              Listen vergraben, sondern dort angezeigt, wo sie steht.
            </p>
            <ul className="mt-6 space-y-3">
              <Bullet>Filtere nach Kategorie und finde jeden Punkt sofort</Bullet>
              <Bullet>Pins bündeln sich automatisch – auch bei hunderten Objekten</Bullet>
              <Bullet>Bild, Adresse und Koordinaten zu jedem Standort</Bullet>
            </ul>
            <button
              onClick={() => navigate("/map")}
              data-testid="showcase-cta-map"
              className="group mt-8 flex items-center gap-2.5 rounded-2xl bg-primary px-7 py-4 text-base font-bold text-white shadow-xl shadow-black/40 transition-transform hover:scale-[1.03] active:scale-[0.97]"
            >
              <Layers size={19} /> Lagekarte erkunden
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
          <div className="go-fade-up overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
            <img
              src={OPS_IMG}
              alt="Team überwacht die digitale Lagekarte einer Veranstaltung"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Category preview */}
      {topCats.length > 0 && (
        <section
          className="mx-auto w-full max-w-6xl px-5 py-16 sm:px-8 sm:py-24"
          data-testid="category-preview"
        >
          <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_12px_40px_rgba(15,23,42,0.05)] sm:p-10">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
                  Auf der Karte
                </p>
                <h2 className="mt-2 font-display text-2xl font-bold text-slate-900">
                  Diese Infrastruktur ist erfasst
                </h2>
              </div>
              <button
                onClick={() => navigate("/map")}
                data-testid="category-cta-map"
                className="flex items-center gap-1.5 text-sm font-bold text-primary hover:underline"
              >
                Alle ansehen <ArrowRight size={15} />
              </button>
            </div>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {topCats.map((c) => (
                <button
                  key={c.name}
                  onClick={() => navigate("/map")}
                  data-testid={`category-chip-${c.name}`}
                  className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 py-2 pl-2 pr-3.5 text-sm font-semibold text-slate-700 transition-colors hover:border-primary/40 hover:bg-amber-50"
                >
                  <span
                    className="grid h-6 w-6 place-items-center rounded-full text-white"
                    style={{ background: catColor(c.name) }}
                  >
                    <CategoryIcon category={c.name} color="#fff" size={13} />
                  </span>
                  {c.name}
                  <span className="text-xs font-bold text-slate-400">{c.count}</span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="mx-auto w-full max-w-6xl px-5 pb-20 sm:px-8">
        <div className="relative overflow-hidden rounded-[2rem] bg-[#070c18] px-8 py-14 text-center shadow-2xl sm:px-12 sm:py-16">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary/20 blur-3xl" />
          <div className="absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-sky-500/10 blur-3xl" />
          <h2 className="relative font-display text-2xl font-extrabold text-white sm:text-4xl">
            Verschaff dir den Überblick.
          </h2>
          <p className="relative mx-auto mt-3 max-w-md text-sm text-slate-300 sm:text-base">
            Öffne die smarte Lagekarte und sieh jede kritische Infrastruktur des
            Geländes in Echtzeit.
          </p>
          <button
            onClick={() => navigate("/map")}
            data-testid="footer-cta-map"
            className="group relative mx-auto mt-8 flex items-center gap-2.5 rounded-2xl bg-primary px-8 py-4 text-base font-bold text-white shadow-xl shadow-black/50 transition-transform hover:scale-[1.03] active:scale-[0.97]"
          >
            <MapIcon size={20} /> Lagekarte öffnen
            <ArrowRight size={19} className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8 text-center text-sm text-slate-400">
        © {new Date().getFullYear()} {title} · Smarte Lagekarte für Outdoor-Veranstaltungen
      </footer>
    </div>
  );
}
